/**
 * LANE V2 — the session stream client (`GET /api/sessions/{s}/events?ticket=…`).
 *
 * Fetch-based SSE, like `../sse.ts`. That module's frame parser is inlined in its read loop (and
 * entangled with run-protocol negotiation), so it is not cleanly reusable; this file has a small PURE
 * parser instead (`createSseParser`) plus pure reconnect rules, and one impure loop that wires them.
 *
 * THE STREAM'S CONTRACT (MSA `routes.py::session_events`):
 *   · `id: <seq>`, `event: <type>`, `data: {seq,type,payload,task_id,run_id,at}`; idle `: keepalive`.
 *   · Resume with `Last-Event-ID: <seq>` → the server replays `seq > cursor`, oldest first.
 *   · The ticket lives 15 min and the server CLOSES the stream when it lapses. That close is normal:
 *     re-mint and reconnect from `lastSeq`. A multi-day task reconnecting hours later works the same
 *     way — the cursor is all the server needs.
 *   · `type` is an open string: unknown types are passed on and ignored by the reducer.
 *
 * Idempotent by seq: a frame with `seq <= lastSeq` is dropped here, before anything sees it.
 */
import { mintTicket, sessionEventsUrl, isLaneV2Error } from "./api";
import { getMultiAgentAuthHeaders } from "../client";
import type { SessionFrameWire } from "./types";
import type { LiveFrameWire } from "./activity";

/** The transient, non-replayable frame types (`live_frames`, contracts 0.2.20). */
const LIVE_TYPES: ReadonlySet<string> = new Set(["turn_activity", "turn_text_delta"]);

// ── pure: SSE parsing ────────────────────────────────────────────────────────────────────────────

/** One dispatched SSE message. `data` is the joined `data:` lines; `null` fields were absent. */
export interface SseMessage {
  id: string | null;
  event: string | null;
  data: string;
}

/**
 * An incremental SSE parser (the WHATWG field rules, minus `retry`): feed it chunks, get messages.
 * Comment lines (`:` prefix — the keepalive) and messages with no `data:` are skipped. Accepts
 * `\n`, `\r\n` and `\r` line endings; strips exactly one space after the colon.
 */
export function createSseParser(): { push(chunk: string): SseMessage[]; flush(): SseMessage[] } {
  let buffer = "";
  let data: string[] = [];
  let id: string | null = null;
  let event: string | null = null;

  const dispatch = (out: SseMessage[]) => {
    if (data.length) out.push({ id, event, data: data.join("\n") });
    data = [];
    id = null;
    event = null;
  };

  const line = (raw: string, out: SseMessage[]) => {
    if (raw === "") {
      dispatch(out);
      return;
    }
    if (raw.startsWith(":")) return; // comment / keepalive
    const colon = raw.indexOf(":");
    const field = colon === -1 ? raw : raw.slice(0, colon);
    let value = colon === -1 ? "" : raw.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "data") data.push(value);
    else if (field === "id") id = value;
    else if (field === "event") event = value;
  };

  return {
    push(chunk: string) {
      const out: SseMessage[] = [];
      buffer += chunk;
      for (;;) {
        const n = buffer.indexOf("\n");
        const r = buffer.indexOf("\r");
        let cut = -1;
        let width = 1;
        if (r !== -1 && (n === -1 || r < n)) {
          // A lone `\r` at the very end may be the first half of `\r\n`: wait for more.
          if (r === buffer.length - 1) break;
          cut = r;
          width = buffer[r + 1] === "\n" ? 2 : 1;
        } else if (n !== -1) {
          cut = n;
        }
        if (cut === -1) break;
        const raw = buffer.slice(0, cut);
        buffer = buffer.slice(cut + width);
        line(raw, out);
      }
      return out;
    },
    flush() {
      const out: SseMessage[] = [];
      if (buffer) {
        line(buffer, out);
        buffer = "";
      }
      dispatch(out);
      return out;
    },
  };
}

/** Parse one message's data into a frame, or null when it is not a well-formed frame. */
export function parseSessionFrame(message: SseMessage): SessionFrameWire | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(message.data);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const f = parsed as Record<string, unknown>;
  const seqFromId = message.id !== null && /^\d+$/.test(message.id) ? Number(message.id) : null;
  const seq = typeof f.seq === "number" ? f.seq : seqFromId;
  const type = typeof f.type === "string" ? f.type : message.event;
  if (seq === null || !Number.isFinite(seq) || !type) return null;
  return {
    seq,
    type,
    payload: f.payload && typeof f.payload === "object" ? (f.payload as Record<string, unknown>) : {},
    task_id: typeof f.task_id === "string" ? f.task_id : null,
    run_id: typeof f.run_id === "string" ? f.run_id : null,
    at: typeof f.at === "string" ? f.at : "",
  };
}

// ── pure: reconnect rules ────────────────────────────────────────────────────────────────────────

export const STREAM_BACKOFF_BASE_MS = 500;
export const STREAM_BACKOFF_CAP_MS = 30_000;
/** Consecutive FAILED attempts before the stream reports `disconnected` and waits for a manual retry. */
export const STREAM_MAX_FAILURES = 12;
/** No byte (keepalives count) for this long = a dead connection. */
export const STREAM_STALL_MS = 60_000;

/** Exponential backoff for failure `n` (1-based), capped. A clean server close uses `n = 0` → short. */
export function streamBackoffMs(failures: number): number {
  if (failures <= 0) return 250;
  return Math.min(STREAM_BACKOFF_CAP_MS, STREAM_BACKOFF_BASE_MS * 2 ** (failures - 1));
}

/** Should a frame be applied, given the cursor? */
/**
 * A live frame (`event: turn_activity`, no `id:`, `n` per turn): parsed separately so it can never
 * be mistaken for a journal frame — it carries no `seq`, must not move `Last-Event-ID`, and is
 * dropped by `parseSessionFrame` by design. `null` for anything else.
 */
export function parseLiveFrame(message: SseMessage): LiveFrameWire | null {
  if (!message.event || !LIVE_TYPES.has(message.event)) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(message.data);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const f = parsed as Record<string, unknown>;
  const type = typeof f.type === "string" ? f.type : message.event;
  const n = typeof f.n === "number" && Number.isFinite(f.n) ? f.n : null;
  if (n === null) return null;
  return {
    type,
    turn_id: typeof f.turn_id === "string" ? f.turn_id : null,
    task_id: typeof f.task_id === "string" ? f.task_id : null,
    n,
    at: typeof f.at === "string" ? f.at : new Date().toISOString(),
    payload: f.payload && typeof f.payload === "object" ? (f.payload as Record<string, unknown>) : {},
  };
}

export function isNewFrame(seq: number, lastSeq: number): boolean {
  return seq > lastSeq;
}

/**
 * What the loop does after a connection ends.
 *   · `clean`   — the server closed the body (the ticket lapsed): re-mint, reconnect promptly.
 *   · `error`   — network/HTTP failure or stall: back off; give up after STREAM_MAX_FAILURES.
 *   · `fatal`   — the session is gone or not ours (404/403 after a fresh ticket): stop.
 */
export type EndKind = "clean" | "error" | "fatal";

export function nextAction(end: EndKind, failures: number): { stop: boolean; delayMs: number; failures: number } {
  if (end === "fatal") return { stop: true, delayMs: 0, failures };
  if (end === "clean") return { stop: false, delayMs: streamBackoffMs(0), failures: 0 };
  const next = failures + 1;
  if (next > STREAM_MAX_FAILURES) return { stop: true, delayMs: 0, failures: next };
  return { stop: false, delayMs: streamBackoffMs(next), failures: next };
}

// ── the loop ─────────────────────────────────────────────────────────────────────────────────────

export type StreamStatus = "idle" | "connecting" | "connected" | "reconnecting" | "disconnected";

export interface SessionStreamHandlers {
  /** Called once per NEW frame (seq > cursor), in order. */
  onFrame: (frame: SessionFrameWire) => void;
  /** Called per transient live frame (`turn_activity`), in arrival order; never affects the cursor. */
  onLive?: (frame: LiveFrameWire) => void;
  onStatus?: (status: StreamStatus, info?: { failures: number; delayMs?: number; error?: string }) => void;
  /** The session is gone / not ours — the caller should stop treating it as live. */
  onFatal?: (error: unknown) => void;
}

export interface SessionStreamDeps {
  fetch?: typeof fetch;
  mintTicket?: (sessionId: string) => Promise<{ ticket: string }>;
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
  stallMs?: number;
}

const abortableSleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal.aborted) return resolve();
    const t = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(t);
      resolve();
    });
  });

/**
 * Open the session stream and keep it open until `abort()`. `getLastSeq` is read at every (re)connect
 * so the cursor always comes from the owner (the store), never a stale copy.
 */
export function openSessionStream(
  sessionId: string,
  getLastSeq: () => number,
  handlers: SessionStreamHandlers,
  deps: SessionStreamDeps = {},
): { abort: () => void } {
  const doFetch = deps.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const mint = deps.mintTicket ?? mintTicket;
  const sleep = deps.sleep ?? abortableSleep;
  const stallMs = deps.stallMs ?? STREAM_STALL_MS;
  const controller = new AbortController();
  let failures = 0;
  let cursor = getLastSeq();

  (async () => {
    let first = true;
    while (!controller.signal.aborted) {
      handlers.onStatus?.(first ? "connecting" : "reconnecting", { failures });
      first = false;
      let end: EndKind = "error";
      let lastError: unknown = null;
      const attempt = new AbortController();
      const relay = () => attempt.abort();
      controller.signal.addEventListener("abort", relay);
      let stallTimer: ReturnType<typeof setTimeout> | null = null;
      const armStall = () => {
        if (stallTimer) clearTimeout(stallTimer);
        stallTimer = setTimeout(() => attempt.abort(), stallMs);
      };
      try {
        const { ticket } = await mint(sessionId);
        cursor = Math.max(cursor, getLastSeq());
        const headers: Record<string, string> = { Accept: "text/event-stream", ...getMultiAgentAuthHeaders() };
        if (cursor > 0) headers["Last-Event-ID"] = String(cursor);
        armStall();
        const res = await doFetch(sessionEventsUrl(sessionId, ticket), {
          method: "GET",
          headers,
          signal: attempt.signal,
        });
        if (!res.ok) {
          // 404: not (or no longer) the caller's session. 403 TICKET_NOT_FOR_SESSION after a fresh
          // mint is not going to fix itself either.
          end = res.status === 404 || res.status === 403 ? "fatal" : "error";
          lastError = new Error(`session stream HTTP ${res.status}`);
        } else {
          const reader = res.body?.getReader();
          if (!reader) throw new Error("session stream: no body");
          handlers.onStatus?.("connected", { failures: 0 });
          const decoder = new TextDecoder();
          const parser = createSseParser();
          let sawBytes = false;
          const deliver = (messages: SseMessage[]) => {
            for (const m of messages) {
              const live = parseLiveFrame(m);
              if (live) {
                handlers.onLive?.(live);
                continue;
              }
              const frame = parseSessionFrame(m);
              if (!frame || !isNewFrame(frame.seq, cursor)) continue;
              cursor = frame.seq;
              handlers.onFrame(frame);
            }
          };
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            armStall();
            if (!sawBytes) {
              sawBytes = true;
              failures = 0; // a connection that delivered anything resets the backoff
            }
            deliver(parser.push(decoder.decode(value, { stream: true })));
          }
          deliver(parser.flush());
          end = "clean";
        }
      } catch (err) {
        lastError = err;
        // A mint refused because the session is not (or no longer) a v2 session of ours.
        if (isLaneV2Error(err) && (err.status === 404 || err.status === 403 || err.code === "WRONG_LANE")) {
          end = "fatal";
        } else {
          end = "error";
        }
      } finally {
        if (stallTimer) clearTimeout(stallTimer);
        controller.signal.removeEventListener("abort", relay);
      }
      if (controller.signal.aborted) break;
      const next = nextAction(end, failures);
      failures = next.failures;
      if (next.stop) {
        handlers.onStatus?.("disconnected", {
          failures,
          error: lastError instanceof Error ? lastError.message : undefined,
        });
        if (end === "fatal") handlers.onFatal?.(lastError);
        return;
      }
      handlers.onStatus?.("reconnecting", { failures, delayMs: next.delayMs });
      await sleep(next.delayMs, controller.signal);
    }
    handlers.onStatus?.("idle", { failures });
  })();

  return { abort: () => controller.abort() };
}
